import { useMemo } from 'react'

import BillingBreakdown from 'components/billing/BillingBreakdown'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import EmployerInvoicesPaths from 'rails_routes/Employers/EmployerInvoicesPaths'
import { INVOICE_TYPE, type InvoiceType, isStripeInvoiceType } from 'utils/billing'
import {
  useGetEmployersEmployerInvoicesId,
  useGetEmployersStripeInvoicesId,
} from 'utils/web_api'

interface InvoiceDetailPageProps {
  invoiceId: string
  invoiceType?: InvoiceType
}

export function InvoiceDetailPage ({ invoiceId, invoiceType: invoiceTypeProp }: InvoiceDetailPageProps) {
  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search)
  }, [])
  const invoiceTypeParam = (invoiceTypeProp || searchParams.get('type')) as InvoiceType | null
  const isStripeInvoice = isStripeInvoiceType(invoiceTypeParam)

  const {
    data: employerInvoiceData,
    isLoading: isEmployerInvoiceLoading,
    isError: isEmployerInvoiceError,
  } = useGetEmployersEmployerInvoicesId(invoiceId, {
    query: {
      enabled: !isStripeInvoice,
    },
  })

  const {
    data: stripeInvoiceData,
    isLoading: isStripeInvoiceLoading,
    isError: isStripeInvoiceError,
  } = useGetEmployersStripeInvoicesId(invoiceId, {
    query: {
      enabled: isStripeInvoice,
    },
  })

  const handleBack = () => {
    window.location.href = EmployerInvoicesPaths.index()
  }

  if (isStripeInvoice) {
    if (isStripeInvoiceLoading) {
      return (
        <div className='flex items-center justify-center py-12'>
          <LoadingSpinnerCentered />
        </div>
      )
    }

    if (isStripeInvoiceError || !stripeInvoiceData) {
      return (
        <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
          <div>
            There was an error fetching invoice details. Please reach out to{' '}
            <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
            continue to experience this error.
          </div>
        </div>
      )
    }

    return (
      <BillingBreakdown
        invoiceId={invoiceId}
        invoiceType={INVOICE_TYPE.STRIPE}
        onBack={handleBack}
        stripeInvoice={stripeInvoiceData}
      />
    )
  }

  if (isEmployerInvoiceLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (isEmployerInvoiceError || !employerInvoiceData) {
    return (
      <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
        <div>
          There was an error fetching invoice details. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  return (
    <BillingBreakdown
      invoiceId={invoiceId}
      invoiceType={INVOICE_TYPE.EMPLOYER}
      onBack={handleBack}
      employerInvoice={employerInvoiceData}
    />
  )
}

export default InvoiceDetailPage
